import { getSettingsData } from '@/features/app/api';
import {
  getBookings,
  getFilteredBookingSessionsPaginate,
} from '@/features/bookings-management/api';
import type {
  Booking,
  BookingSession,
} from '@/features/bookings-management/types';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import { getDeepLinkedSessionFilter } from '@/features/bookings-management/utils/bookingSessions';
import { clientManagementPath } from '@/features/users/clients/routes/paths';
import { providersPath } from '@/features/users/providers/routes/paths';
import { getClients, getProviders } from '@/features/users/shared/api';
import type { User } from '@/features/users/shared/types';
import type { Settings } from '@/types/settings';
import type { StatusWithSyleClass } from '@/types';

export type GlobalSearchResultType =
  | 'client'
  | 'provider'
  | 'booking'
  | 'booking_session';

export type GlobalSearchResult = {
  id: number;
  type: GlobalSearchResultType;
  title: string;
  subtitle: string;
  meta: string;
  href: string;
};

export type GlobalSearchCategory = {
  type: GlobalSearchResultType;
  label: string;
  results: GlobalSearchResult[];
  error?: string;
};

export type GlobalSearchPermissions = {
  canSearchClients: boolean;
  canSearchProviders: boolean;
  canSearchBookings: boolean;
};

type SearchGlobalEntitiesParams = {
  term: string;
  permissions: GlobalSearchPermissions;
  limit?: number;
};

type SearchableCandidate = {
  id: number;
  searchText: string;
  tokens: string[];
  originalIndex: number;
};

const RESULT_LIMIT = 5;
const SESSION_FILTERS = [
  'previous',
  'today',
  'upcoming',
  'unassigned',
  'cancelled',
];

export const normalizeGlobalSearchTerm = (
  value: string | number | null | undefined
) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const normalizeSearchValue = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const fullName = (person?: Pick<User, 'first_name' | 'last_name'> | null) =>
  [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim();

const compactJoin = (values: unknown[]) =>
  values
    .flatMap((value) => {
      if (Array.isArray(value)) return value;
      return [value];
    })
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .join(' ');

const getAddressSearchText = (address: any) =>
  compactJoin([
    address?.name,
    address?.formatted_address,
    address?.postcode,
    address?.postcode?.replace(/\s/g, ''),
    address?.town_or_city,
  ]);

const getUserTokens = (user: User) => [
  String(user.id),
  user.first_name,
  user.last_name,
  fullName(user),
  user.email,
  user.phone,
  user.user_statuses?.display,
  user.user_statuses?.value,
  ...(user.address ?? []).map(getAddressSearchText),
];

const getBookingTokens = (booking: Booking) => [
  String(booking.id),
  fullName(booking.user),
  booking.user?.email,
  getAddressSearchText(booking.address),
  booking.service_variant?.name,
  booking.service_variant?.service?.name,
  booking.booking_status?.display,
  booking.booking_status?.value,
  booking.total_amount,
  ...(booking.booking_cleaners ?? []).map(fullName),
];

const getSessionTokens = (session: BookingSession) => [
  String(session.id),
  String(session.booking_id),
  fullName(session.user),
  session.user?.email,
  getAddressSearchText(session.address),
  session.date,
  session.time,
  session.service_variant?.name,
  session.service_variant?.service?.name,
  session.booking_session_status?.display,
  session.booking_session_status?.value,
  session.booking_session_payment_status?.display,
  session.booking_session_payment_status?.value,
  ...(session.booking_session_cleaners ?? []).map(fullName),
];

export const rankGlobalSearchCandidate = (
  term: string,
  candidate: SearchableCandidate
) => {
  const normalizedTerm = normalizeGlobalSearchTerm(term);

  if (!normalizedTerm) return Number.MAX_SAFE_INTEGER;
  if (/^\d+$/.test(normalizedTerm) && String(candidate.id) === normalizedTerm) {
    return 0;
  }
  if (
    candidate.tokens.some((token) =>
      normalizeSearchValue(token).startsWith(normalizedTerm)
    )
  ) {
    return 1;
  }
  if (candidate.searchText.includes(normalizedTerm)) {
    return 2;
  }

  return 3;
};

export const dedupeGlobalSearchResults = <T extends { id: number }>(
  items: T[]
) => {
  const seenIds = new Set<number>();

  return items.filter((item) => {
    if (seenIds.has(item.id)) return false;
    seenIds.add(item.id);
    return true;
  });
};

const filterAndRankItems = <T extends { id: number }>(
  term: string,
  items: T[],
  getTokens: (item: T) => unknown[]
) =>
  dedupeGlobalSearchResults(items)
    .map((item, originalIndex) => {
      const tokens = getTokens(item).map((token) => String(token ?? ''));
      const candidate = {
        id: item.id,
        tokens,
        searchText: normalizeSearchValue(compactJoin(tokens)),
        originalIndex,
      };

      return {
        item,
        rank: rankGlobalSearchCandidate(term, candidate),
        originalIndex,
      };
    })
    .filter(({ rank }) => rank < 3)
    .sort(
      (first, second) =>
        first.rank - second.rank || first.originalIndex - second.originalIndex
    )
    .map(({ item }) => item);

const getSearchableStatuses = (settings: Settings) =>
  settings.userStatuses.filter((status) => {
    const normalizedDisplay = status.display.toLowerCase();
    const normalizedValue = status.value.toLowerCase();

    return normalizedDisplay !== 'removed' && normalizedValue !== 'removed';
  });

const statusLabel = (
  status?: StatusWithSyleClass | Pick<StatusWithSyleClass, 'display' | 'value'>
) => status?.display ?? status?.value ?? 'N/A';

export const getGlobalSearchResultHref = (
  resultType: GlobalSearchResultType,
  entity: User | Booking | BookingSession
) => {
  switch (resultType) {
    case 'client':
      return clientManagementPath('edit', {
        userId: String(entity.id),
        tabSid: 'details',
      });
    case 'provider':
      return providersPath('edit', {
        userId: String(entity.id),
        tabSid: 'details',
      });
    case 'booking':
      return bookingsManagementPath('view', {
        bookingId: String(entity.id),
        bookingTabSid: 'booking-sessions',
        sessionFilter: 'today',
      });
    case 'booking_session': {
      const session = entity as BookingSession;
      const sessionFilter = getDeepLinkedSessionFilter(session);
      return `${bookingsManagementPath('view', {
        bookingId: String(session.booking_id),
        bookingTabSid: 'booking-sessions',
        sessionFilter,
      })}?sessionID=${session.id}`;
    }
    default:
      return '#';
  }
};

export const createClientGlobalSearchResult = (
  client: User
): GlobalSearchResult => ({
  id: client.id,
  type: 'client',
  title: fullName(client) || `Client #${client.id}`,
  subtitle: compactJoin([client.email, client.phone]) || 'No contact details',
  meta: statusLabel(client.user_statuses),
  href: getGlobalSearchResultHref('client', client),
});

export const createProviderGlobalSearchResult = (
  provider: User
): GlobalSearchResult => ({
  id: provider.id,
  type: 'provider',
  title: fullName(provider) || `Provider #${provider.id}`,
  subtitle:
    compactJoin([provider.email, provider.phone]) || 'No contact details',
  meta: statusLabel(provider.user_statuses),
  href: getGlobalSearchResultHref('provider', provider),
});

export const createBookingGlobalSearchResult = (
  booking: Booking
): GlobalSearchResult => ({
  id: booking.id,
  type: 'booking',
  title: `Booking #${booking.id}`,
  subtitle:
    compactJoin([
      fullName(booking.user),
      booking.address?.postcode,
      booking.service_variant?.name,
    ]) || 'Booking',
  meta: statusLabel(booking.booking_status),
  href: getGlobalSearchResultHref('booking', booking),
});

export const createBookingSessionGlobalSearchResult = (
  session: BookingSession
): GlobalSearchResult => ({
  id: session.id,
  type: 'booking_session',
  title: `Session #${session.id}`,
  subtitle:
    compactJoin([
      `Booking #${session.booking_id}`,
      fullName(session.user),
      session.date,
      session.time,
    ]) || 'Booking session',
  meta: statusLabel(session.booking_session_status),
  href: getGlobalSearchResultHref('booking_session', session),
});

const searchUsersByStatus = async (
  statuses: StatusWithSyleClass[],
  getUsers: (statusId: number) => Promise<User[]>
) => {
  const usersByStatus = await Promise.all(
    statuses.map((status) => getUsers(status.id))
  );

  return usersByStatus.flat();
};

const withSearchError = async (
  type: GlobalSearchResultType,
  label: string,
  search: () => Promise<GlobalSearchResult[]>
): Promise<GlobalSearchCategory> => {
  try {
    return {
      type,
      label,
      results: await search(),
    };
  } catch (_error) {
    return {
      type,
      label,
      results: [],
      error: `Unable to search ${label.toLowerCase()}`,
    };
  }
};

export async function searchGlobalEntities({
  term,
  permissions,
  limit = RESULT_LIMIT,
}: SearchGlobalEntitiesParams): Promise<GlobalSearchCategory[]> {
  const normalizedTerm = normalizeGlobalSearchTerm(term);
  const needsUserStatuses =
    permissions.canSearchClients || permissions.canSearchProviders;
  const settingsPromise = needsUserStatuses
    ? getSettingsData()
    : Promise.resolve<Settings | null>(null);

  const searchTasks: Promise<GlobalSearchCategory>[] = [];

  if (permissions.canSearchClients) {
    searchTasks.push(
      withSearchError('client', 'Clients', async () => {
        const settings = await settingsPromise;
        const statuses = settings ? getSearchableStatuses(settings) : [];
        const clients = await searchUsersByStatus(statuses, getClients);

        return filterAndRankItems(normalizedTerm, clients, getUserTokens)
          .slice(0, limit)
          .map(createClientGlobalSearchResult);
      })
    );
  }

  if (permissions.canSearchProviders) {
    searchTasks.push(
      withSearchError('provider', 'Providers', async () => {
        const settings = await settingsPromise;
        const statuses = settings ? getSearchableStatuses(settings) : [];
        const providers = await searchUsersByStatus(statuses, getProviders);

        return filterAndRankItems(normalizedTerm, providers, getUserTokens)
          .slice(0, limit)
          .map(createProviderGlobalSearchResult);
      })
    );
  }

  if (permissions.canSearchBookings) {
    searchTasks.push(
      withSearchError('booking', 'Bookings', async () => {
        const { data: bookings } = await getBookings({
          page: 1,
          limit,
          search: normalizedTerm,
        });

        return filterAndRankItems(normalizedTerm, bookings, getBookingTokens)
          .slice(0, limit)
          .map(createBookingGlobalSearchResult);
      })
    );

    searchTasks.push(
      withSearchError('booking_session', 'Booking Sessions', async () => {
        const responses = await Promise.all(
          SESSION_FILTERS.map((sessionFilter) =>
            getFilteredBookingSessionsPaginate({
              sessionFilter,
              page: 1,
              limit,
              search: normalizedTerm,
            })
          )
        );
        const sessions = responses.flatMap((response) => response.data);

        return filterAndRankItems(normalizedTerm, sessions, getSessionTokens)
          .slice(0, limit)
          .map(createBookingSessionGlobalSearchResult);
      })
    );
  }

  return Promise.all(searchTasks);
}
